/* ---------------------------------------------------------------------------
   Speed optimization v2.2 (Aug 2026): defer offscreen video downloads.

   Improvements over v1 (both LCP- and INP-motivated):
   - The initial detach pass is CHUNKED: 6 videos per ~40ms timer tick instead
     of all ~50 in one synchronous block at DOMContentLoaded. The old single
     block could delay first render on mid-range phones (hurting LCP).
     Timer-based (not rAF) so it also completes in background tabs.
   - The fallback position checker no longer polls every second (the old
     interval forced a layout recalculation each tick, competing with user
     interactions and hurting INP). It now runs only after actual scrolling,
     throttled to at most one check per 350ms.
   - Restores are capped at 3 per tick so re-attaching many videos never
     produces one long main-thread task.

   v2.2 adds: rendition fallback. Card templates downshift video URLs to the
   480p rendition (see snippets/card.liquid and card-pro-gallery-slides).
   If such a file were ever missing on the CDN, the error handler below
   swaps the source back to the original URL kept in data-hq-fallback, so
   a card video can never end up broken.

   Behavior is unchanged: videos far below the fold (or hidden in inactive
   slides) do not download until the visitor approaches; they re-attach and
   autoplay before entering the viewport. Near-viewport videos are never
   touched. Fully reversible by clearing this file.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* --- rendition fallback (runs for all videos, immediately) ------------- */
  document.addEventListener('error', function (e) {
    var t = e.target;
    if (!t || t.tagName !== 'SOURCE') return;
    var fb = t.getAttribute('data-hq-fallback');
    if (!fb || t.getAttribute('data-fb-done')) return;
    t.setAttribute('data-fb-done', '1');
    t.setAttribute('src', fb);
    var v = t.parentElement;
    if (v && v.tagName === 'VIDEO') {
      try { v.load(); } catch (err) {}
      if (v.autoplay) {
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    }
  }, true);

  function init() {
    var vids = Array.prototype.slice.call(document.querySelectorAll('video'));
    if (!vids.length) return;

    var store = new WeakMap();
    var deferredList = [];

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) restore(en.target);
        });
      }, { rootMargin: '150% 0px 150% 0px' });
    }

    function strip(v) {
      if (store.has(v)) return;
      var sources = Array.prototype.slice.call(v.querySelectorAll('source'));
      var srcAttr = v.getAttribute('src');
      if (!srcAttr && sources.length === 0) return;
      store.set(v, {
        src: srcAttr,
        sources: sources,
        wasAutoplay: v.autoplay || v.hasAttribute('autoplay') || !v.paused
      });
      deferredList.push(v);
      try { v.pause(); } catch (e) {}
      if (srcAttr) v.removeAttribute('src');
      sources.forEach(function (s) { v.removeChild(s); });
      try { v.load(); } catch (e) {}
      v.setAttribute('data-perf-deferred', '');
      if (io) io.observe(v);
    }

    function restore(v) {
      var data = store.get(v);
      if (!data) return;
      store.delete(v);
      var i = deferredList.indexOf(v);
      if (i !== -1) deferredList.splice(i, 1);
      if (io) io.unobserve(v);
      data.sources.forEach(function (s) { v.appendChild(s); });
      if (data.src) v.setAttribute('src', data.src);
      try { v.load(); } catch (e) {}
      v.removeAttribute('data-perf-deferred');
      if (data.wasAutoplay) {
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    }

    /* --- phase 1: measure once, then detach in small timed chunks -------- */
    var vh = window.innerHeight || 800;
    var toStrip = [];
    vids.forEach(function (v) {
      var r = v.getBoundingClientRect(); /* single batched read, layout is clean here */
      var hidden = r.width === 0 && r.height === 0;
      var far = r.top > vh * 1.5 || r.bottom < -(vh * 1.5);
      if (far || hidden) toStrip.push(v);
    });

    var idx = 0;
    function stripChunk() {
      var n = 0;
      while (idx < toStrip.length && n < 6) { strip(toStrip[idx++]); n++; }
      if (idx < toStrip.length) setTimeout(stripChunk, 40);
    }
    if (toStrip.length) stripChunk();

    /* --- phase 2: scroll-driven fallback (no idle polling) --------------- */
    var checkQueued = false;
    function checkNear() {
      checkQueued = false;
      if (!deferredList.length) return;
      var vh2 = window.innerHeight || 800;
      var restored = 0;
      var snapshot = deferredList.slice();
      for (var i = 0; i < snapshot.length && restored < 3; i++) {
        var v = snapshot[i];
        var r = v.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) continue; /* hidden slides: IO handles */
        if (r.top < vh2 * 2.5 && r.bottom > -(vh2 * 1.5)) { restore(v); restored++; }
      }
      if (restored === 3 && deferredList.length) queueCheck(); /* more may be near */
    }
    function queueCheck() {
      if (checkQueued) return;
      checkQueued = true;
      setTimeout(checkNear, 50);
    }
    var lastScrollCheck = 0;
    window.addEventListener('scroll', function () {
      var now = Date.now();
      if (now - lastScrollCheck < 350) return;
      lastScrollCheck = now;
      queueCheck();
    }, { passive: true });
    /* one delayed safety check for browsers/states where nothing scrolled yet */
    setTimeout(queueCheck, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
